import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { connect, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getLabelMessage } from '@gef-ui/localization/messages';

import CollapsibleCard from 'components/atoms/GefMui/CollapsibleCard';
import { ComponentLoader } from 'components/index';
import fetch from 'features/common/fetch';
import { getVisibleStateById } from 'features/common/Subheader/selectors';
import { fetchAgenciesForCampaignRegionNominations } from 'features/other/organizationStructureMUI/agency/actions';
import { getAgencies } from 'features/other/organizationStructureMUI/agency/selectors';
import campaignNominations from 'features/views/campaignNominations';
import { getCampaignRegionNominationsFilter } from 'features/views/campaignRegionNomination/selectors';
import { submitFilter } from 'features/views/campaignSubsellerNomination/actions';
import { getRoutePath } from 'utils';
import { joinString } from 'utils/string';

import RegionItemHeader from './RegionItemHeader';

const Region = (props) => {
	const {
		agencies,
		region,
		count,
		regionId,
		isTheOnlyRegion,
		campaignDetail,
		fetchAgenciesForCampaignRegionNominations,
		fetchNominationsCount,
		submitSubsellerNominationsFilter,
	} = props;
	const navigate = useNavigate();
	const { id: campaignId, channelId } = campaignDetail;

	const [countsByLayer, setCountsByLayer] = useState({});
	const visibleState = useSelector((state) => getVisibleStateById(state, region.id, isTheOnlyRegion));

	const loadData = useCallback(() => {
		fetchAgenciesForCampaignRegionNominations(channelId, regionId);
		fetchNominationsCount(campaignId, { regionId }, `regionId.${regionId}`).then((nominationCountsByLayer) => {
			setCountsByLayer(nominationCountsByLayer);
		});
	}, [channelId, regionId, campaignId, fetchAgenciesForCampaignRegionNominations, fetchNominationsCount]);

	useEffect(() => {
		if (visibleState) {
			loadData();
		}
	}, [loadData, visibleState]);

	const handleReload = useCallback(() => {
		loadData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campaignId]);

	const handleSetSubsellerNominationsFilter = useCallback(
		(agencyId) => () => {
			submitSubsellerNominationsFilter({
				manager: null,
				agency: agencyId,
				region: regionId,
				channel: channelId,
			});
			const { pathname, state } = getRoutePath('campaignSubsellerNomination', { campaignId }, true);
			navigate(pathname, { state });
		},
		[campaignId, regionId, channelId, navigate, submitSubsellerNominationsFilter],
	);

	return (
		<CollapsibleCard
			id={region.id}
			initiallyOpen={isTheOnlyRegion}
			header={<RegionItemHeader region={region} count={count} />}
		>
			<ComponentLoader
				fetchStorePath={`${fetch.constants.urls.campaignNominationsCount}.regionId.${regionId}`}
				onReload={handleReload}
			>
				<Table>
					<TableHead>
						<TableRow
							sx={{
								'& th': {
									backgroundColor: 'transparent',
								},
							}}
						>
							<TableCell sx={{ pl: 7.5 }}>
								{getLabelMessage('campaignRegionNomination:tableAgencyHeader')}
							</TableCell>
							<TableCell sx={{ pr: 5 }} align="right">
								{getLabelMessage('campaignRegionNomination:tableNominationsHeader')}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody
						sx={{
							'& td': {
								borderBottom: 'unset',
								borderTop: '1px solid rgba(224, 224, 224, 1)',
							},
						}}
					>
						{agencies.map((agency) => (
							<TableRow key={agency.id}>
								<TableCell sx={{ pl: 7.5, '& a': { color: 'primary.main' } }}>
									<Link onClick={handleSetSubsellerNominationsFilter(agency.id)} underline="none">
										{joinString(' - ', [agency?.id, agency?.name])}
									</Link>
								</TableCell>
								<TableCell sx={{ pr: 5 }} align="right">
									{countsByLayer[agency.id]}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</ComponentLoader>
		</CollapsibleCard>
	);
};

Region.propTypes = {
	agencies: PropTypes.array,
	campaignDetail: PropTypes.object,
	channelId: PropTypes.number,
	count: PropTypes.number,
	fetchAgenciesForCampaignRegionNominations: PropTypes.func,
	fetchNominationsCount: PropTypes.func,
	isTheOnlyRegion: PropTypes.bool,
	region: PropTypes.object,
	regionId: PropTypes.number,
	submitSubsellerNominationsFilter: PropTypes.func,
};

export default connect(
	(state, ownProps) => {
		const { campaignDetail, region } = ownProps;
		const regionNominationsFilter = getCampaignRegionNominationsFilter(state);
		let agencies = getAgencies(state, { channel: campaignDetail.channelId, region: region.id });
		if (regionNominationsFilter.agency) {
			agencies = agencies.filter((agency) => {
				return agency.id === regionNominationsFilter.agency;
			});
		}
		return {
			agencies,
			regionId: ownProps.region.id,
			regionNominationsFilter,
		};
	},
	{
		fetchAgenciesForCampaignRegionNominations,
		fetchNominationsCount: campaignNominations.actions.fetchNominationsCount,
		submitSubsellerNominationsFilter: submitFilter,
	},
)(Region);
